import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  Filter,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  ChevronDown,
  IndianRupee,
  Download,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

const PROVIDER_COLORS = {
  openai: "#10A37F",
  anthropic: "#F97316",
  google: "#1D4ED8",
  groq: "#4338CA",
};

const PROVIDER_NAMES = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  groq: "Groq",
};

export default function UsageHistoryPage({ api }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [providerFilter, setProviderFilter] = useState("all");
  const [taskFilter, setTaskFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [usdToInr, setUsdToInr] = useState(85.5);
  const [exporting, setExporting] = useState(false);
  const limit = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (providerFilter !== "all") params.append("provider", providerFilter);
      if (taskFilter !== "all") params.append("task_type", taskFilter);
      params.append("limit", limit);
      params.append("skip", page * limit);
      const [logsRes, rateRes] = await Promise.all([
        axios.get(`${api}/usage?${params.toString()}`),
        axios.get(`${api}/exchange-rate`),
      ]);
      setLogs(logsRes.data.logs || []);
      setTotal(logsRes.data.total || 0);
      setUsdToInr(rateRes.data.usd_to_inr || 85.5);
    } catch (err) {
      console.error("Failed to fetch usage logs", err);
    } finally {
      setLoading(false);
    }
  }, [api, providerFilter, taskFilter, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (providerFilter !== "all") params.append("provider", providerFilter);
      if (taskFilter !== "all") params.append("task_type", taskFilter);
      const res = await axios.get(`${api}/usage/export/csv?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `llm_hub_usage_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully!");
    } catch (err) {
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [providerFilter, taskFilter]);

  const filteredLogs = searchQuery
    ? logs.filter(
        (l) =>
          l.model?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          l.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          l.prompt?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : logs;

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="main-content-inner">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Usage History</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {total} total requests logged
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            disabled={exporting || total === 0}
            className="gap-2"
            data-testid="export-csv-button"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="hidden sm:inline">Export</span> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchLogs} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="flex-1 min-w-0">
              <Input
                placeholder="Search by model, request ID, or prompt..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9"
                data-testid="usage-filters-search-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={providerFilter} onValueChange={setProviderFilter} data-testid="usage-filters-provider-select">
                <SelectTrigger className="w-[130px] sm:w-[150px] h-9">
                  <SelectValue placeholder="Provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Providers</SelectItem>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="anthropic">Anthropic</SelectItem>
                  <SelectItem value="google">Google</SelectItem>
                  <SelectItem value="groq">Groq</SelectItem>
                </SelectContent>
              </Select>
              <Select value={taskFilter} onValueChange={setTaskFilter} data-testid="usage-filters-task-select">
                <SelectTrigger className="w-[130px] sm:w-[150px] h-9">
                  <SelectValue placeholder="Task Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="code">Code</SelectItem>
                  <SelectItem value="summarize">Summarize</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
        <Table data-testid="usage-history-table">
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Task</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Tokens</TableHead>
              <TableHead className="text-right">Cost (USD)</TableHead>
              <TableHead className="text-right">Cost (INR)</TableHead>
              <TableHead className="text-right">Latency</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <TableRow key={i}>
                  {Array(9).fill(0).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 bg-muted rounded animate-pulse w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                  No usage logs found.
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs.map((log, index) => (
                <TableRow
                  key={log.id || index}
                  data-testid="usage-history-row"
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => { setSelectedLog(log); setDetailsOpen(true); }}
                >
                  <TableCell className="text-xs">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: PROVIDER_COLORS[log.provider] }}
                      />
                      <span className="text-xs font-medium">{PROVIDER_NAMES[log.provider] || log.provider}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[11px]">{log.task_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <code className="font-mono text-[11px]">{log.model}</code>
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs tabular-nums">
                    {log.usage?.total_tokens?.toLocaleString() || 0}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs tabular-nums">
                    ${log.estimated_cost?.toFixed(6) || "0.000000"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs tabular-nums">
                    ₹{((log.estimated_cost || 0) * usdToInr).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs tabular-nums">
                    {log.latency ? `${log.latency}s` : "-"}
                  </TableCell>
                  <TableCell>
                    {log.status === "success" ? (
                      <Badge variant="secondary" className="text-[11px] text-green-700 bg-green-50 gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        OK
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[11px] text-red-700 bg-red-50 gap-1">
                        <XCircle className="w-3 h-3" />
                        Error
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-xs text-muted-foreground">
              Page {page + 1} of {totalPages} ({total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                className="h-8 text-xs"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
                className="h-8 text-xs"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen} data-testid="usage-history-details-drawer">
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm">Request Details</DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase">Request ID</p>
                  <code className="font-mono text-xs">{selectedLog.id}</code>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase">Provider</p>
                  <p className="text-sm font-medium">{PROVIDER_NAMES[selectedLog.provider]}</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase">Model</p>
                  <code className="font-mono text-xs">{selectedLog.model}</code>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase">Task Type</p>
                  <Badge variant="outline" className="text-xs">{selectedLog.task_type}</Badge>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase">Tokens</p>
                  <p className="text-sm font-mono tabular-nums">
                    {selectedLog.usage?.prompt_tokens || 0} in / {selectedLog.usage?.completion_tokens || 0} out / {selectedLog.usage?.total_tokens || 0} total
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase">Cost / Latency</p>
                  <p className="text-sm font-mono tabular-nums">
                    ${selectedLog.estimated_cost?.toFixed(6)} (₹{((selectedLog.estimated_cost || 0) * usdToInr).toFixed(2)}) / {selectedLog.latency}s
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-[11px] text-muted-foreground uppercase mb-1">Prompt</p>
                <ScrollArea className="max-h-[150px]">
                  <p className="text-sm bg-muted p-3 rounded-lg whitespace-pre-wrap">
                    {selectedLog.prompt || "N/A"}
                  </p>
                </ScrollArea>
              </div>

              {selectedLog.output && (
                <div>
                  <p className="text-[11px] text-muted-foreground uppercase mb-1">Output</p>
                  <ScrollArea className="max-h-[200px]">
                    <p className="text-sm bg-muted p-3 rounded-lg whitespace-pre-wrap font-mono text-xs">
                      {selectedLog.output}
                    </p>
                  </ScrollArea>
                </div>
              )}

              {selectedLog.error && (
                <div>
                  <p className="text-[11px] text-red-500 uppercase mb-1">Error</p>
                  <p className="text-sm bg-red-50 text-red-700 p-3 rounded-lg">{selectedLog.error}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
